import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { RiskBadge } from "@/components/RiskBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  FileText, 
  Ship, 
  Building2, 
  Eye, 
  Download,
  Calendar,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const ReportHistoryPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/reports`);
      setReports(res.data);
    } catch (error) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async (reportId, vesselName) => {
    try {
      const res = await axios.get(`${API}/reports/${reportId}/export/excel`, {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Compliance_${vesselName || "Report"}_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Excel exported successfully");
    } catch (error) {
      toast.error("Failed to export Excel");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getReportTitle = (report) => {
    if (report.vessel_data?.vessel_name) {
      return report.vessel_data.vessel_name;
    }
    if (report.vessel_screening?.entity_name) {
      return report.vessel_screening.entity_name;
    }
    return "Compliance Report";
  };

  const getReportType = (report) => {
    if (report.vessel_data) {
      return { label: "Vessel", icon: Ship, color: "text-[#0b3e65]" };
    }
    return { label: "Company", icon: Building2, color: "text-[#f37022]" };
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="report-history-page">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-serif text-[#0b3e65] flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Compliance Report History
              </CardTitle>
              <span className="text-sm text-slate-500">
                {reports.length} report{reports.length !== 1 ? "s" : ""}
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-3" />
                <p className="text-slate-500">Loading reports...</p>
              </div>
            ) : reports.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>IMO / ID</TableHead>
                    <TableHead>Risk Level</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((report) => {
                    const type = getReportType(report);
                    const TypeIcon = type.icon;
                    return (
                      <TableRow key={report.id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <TypeIcon className={`w-4 h-4 ${type.color}`} />
                            <span className="text-sm">{type.label}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {getReportTitle(report)}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {report.vessel_data?.imo_number || "-"}
                        </TableCell>
                        <TableCell>
                          <RiskBadge risk={report.overall_risk} size="sm" />
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(report.created_at)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Link to={`/reports/${report.id}`}>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                data-testid={`view-report-${report.id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleExportExcel(report.id, getReportTitle(report))}
                              data-testid={`export-report-${report.id}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Excel
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="p-12 text-center">
                <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No reports yet</h3>
                <p className="text-slate-500 mb-4">
                  Start by searching for a vessel or company to create your first compliance report.
                </p>
                <div className="flex justify-center gap-3">
                  <Link to="/vessels">
                    <Button className="bg-[#0b3e65] hover:bg-[#0b3e65]/90 rounded-sm">
                      <Ship className="w-4 h-4 mr-2" />
                      Search Vessel
                    </Button>
                  </Link>
                  <Link to="/companies">
                    <Button variant="outline" className="rounded-sm">
                      <Building2 className="w-4 h-4 mr-2" />
                      Search Company
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ReportHistoryPage;
